import { catchError, map, Observable, of } from 'rxjs';

export type EnrichedResult<T> =
  | {
    success: true;
    data: T;
  }
  | ErrorResponse;

export type ErrorResponse = {
  success: false;
  statusCode: number;
  errors: WellKnownError[];
};

export enum WKECodes {
  LaborCostNotSuitable = 'WKE:C:001',
  NoLaborCostRateForDates = 'WKE:C:002',
  RegisterCostNotEligibleEstimates = 'WKE:C:003',
  RegisterCostNotSuitableEstimates = 'WKE:C:004',
  CreateEstimatedCostNotDraftBudgetVersion = 'WKE:C:005',
  CreateEstimatedCostCannotOvercomeBudget = 'WKE:C:006',
  DeleteEstimatedCostNotDraftBudgetVersion = 'WKE:C:007',
  UserNotEnabledToLogHoursInTheProject = 'WKE:C:008',

  // Attachments
  ClaimDeletedAttachment = 'WKE:ATT:001',

  // Budget
  DisallowedMultiplebudgets = 'WKE:B:001',

  // Budget versions
  ActivateNotCurrentBudgetVersion = 'WKE:BV:001',
  ActivateNotDraftBudgetVersion = 'WKE:BV:002',
  SetCurrentNotDraftBudgetVersion = 'WKE:BV:003',
  UpdateBudgetVersionNotDraft = 'WKE:BV:004',
  UpdateBudgetVersionCannotOvercomeBudget = 'WKE:BV:005',
  UpdateBudgetVersionEstimatesOutOfRange = 'WKE:BV:006',

  // Work package
  DeactivateWPStatusWhenDefault = 'WKE:WP:001',
  WPTypeMissingDefaultStatusExists = 'WKE:WP:002',
  WPWithATypeInARemovedStatusExists = 'WKE:WP:003',
  UpdateWorkPackagePositionDisallowedMove = 'WKE:WP:004',

  // WorkPackage Templates
  TemplateAlreadyExists = 'WKE:TE:001',

  // Unknown
  Unknown = 'Unknown',
}

export const translateWellKnownError = (error: WellKnownError) => {
  switch (error.error) {
    case WKECodes.LaborCostNotSuitable:
      return 'i18n.WellKnownError.laborCostNotSuitable';
    case WKECodes.NoLaborCostRateForDates:
      return 'i18n.WellKnownError.noLaborCostRateForDates';
    case WKECodes.RegisterCostNotEligibleEstimates:
      return 'i18n.WellKnownError.registerCostNotEligibleEstimates';
    case WKECodes.RegisterCostNotSuitableEstimates:
      return 'i18n.WellKnownError.registerCostNotSuitableEstimates';
    case WKECodes.CreateEstimatedCostNotDraftBudgetVersion:
      return 'i18n.WellKnownError.createEstimatedCostNotDraftBudgetVersion';
    case WKECodes.CreateEstimatedCostCannotOvercomeBudget:
      return 'i18n.WellKnownError.createEstimatedCostCannotOvercomeBudget';
    case WKECodes.DeleteEstimatedCostNotDraftBudgetVersion:
      return 'i18n.WellKnownError.deleteEstimatedCostNotDraftBudgetVersion';

    // Budget
    case WKECodes.DisallowedMultiplebudgets:
      return 'i18n.WellKnownError.disallowedMultiplebudgets';

    // Budget versions
    case WKECodes.ActivateNotCurrentBudgetVersion:
      return 'i18n.WellKnownError.activateNotCurrentBudgetVersion';
    case WKECodes.ActivateNotDraftBudgetVersion:
      return 'i18n.WellKnownError.activateNotDraftBudgetVersion';
    case WKECodes.SetCurrentNotDraftBudgetVersion:
      return 'i18n.WellKnownError.setCurrentNotDraftBudgetVersion';
    case WKECodes.UpdateBudgetVersionNotDraft:
      return 'i18n.WellKnownError.updateBudgetVersionNotDraft';
    case WKECodes.UpdateBudgetVersionCannotOvercomeBudget:
      return 'i18n.WellKnownError.updateBudgetVersionCannotOvercomeBudget';
    case WKECodes.UpdateBudgetVersionEstimatesOutOfRange:
      return 'i18n.WellKnownError.updateBudgetVersionEstimatesOutOfRange';

    // Work package
    case WKECodes.DeactivateWPStatusWhenDefault:
      return 'i18n.WellKnownError.deactivateWPStatusWhenDefault';
    case WKECodes.WPTypeMissingDefaultStatusExists:
      return 'i18n.WellKnownError.wPTypeMissingDefaultStatusExists';
    case WKECodes.WPWithATypeInARemovedStatusExists:
      return 'i18n.WellKnownError.wPWithATypeInARemovedStatusExists';

    case WKECodes.UpdateWorkPackagePositionDisallowedMove:
      return 'i18n.WellKnownError.updateWorkPackagePositionDisallowedMove';
    case WKECodes.UserNotEnabledToLogHoursInTheProject:
      return 'i18n.WellKnownError.userNotEnabledToLogHoursInTheProject';
    case WKECodes.ClaimDeletedAttachment:
      return 'i18n.WellKnownError.claimDeletedAttachment';
    case WKECodes.TemplateAlreadyExists:
      return 'i18n.WellKnownError.templateAlreadyExists';
    case WKECodes.Unknown:
      return 'i18n.WellKnownError.unknown';
  }
};

export type WellKnownError = { error: WKECodes; code: string; message?: string };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseError = (response: any): WellKnownError[] => {
  const errors: WellKnownError[] = [];
  for (const [code, message] of Object.entries(response?.error?.errors ?? {})) {
    const wke = Object.values(WKECodes).find((c) => code === c);
    errors.push({
      error: (wke ?? WKECodes.Unknown) as WKECodes,
      code: code,
      message: typeof message === 'string' && message !== '' ? message : undefined,
    });
  }
  return errors;
};

export const hasError = ({ errors }: ErrorResponse, code: WKECodes) => errors.find((e) => e.code === code);

export const hasStatus = ({ statusCode }: ErrorResponse, status: number) => statusCode === status;

export function enrichResult<T>(): (source$: Observable<T>) => Observable<EnrichedResult<T>> {
  return (source$) =>
    source$.pipe(
      map((data) => ({ success: true, data }) as EnrichedResult<T>),
      catchError((response) =>
        of({ success: false, errors: parseError(response), statusCode: response.status as number } as ErrorResponse),
      ),
    );
}
